// Wallet (spec/15 P-15): a wallet per venue brand (organisation), with what it holds and its statement, a page at a
// time. Topping up is at the counter (no online payment is live), said plainly with no button that cannot work.
// Passes and Orders sit with the Wallet: on a phone the three share one tab, linked by `MoneyTabs`.

import type { MyWallet } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, EmptyState, SegmentedControl, Skeleton } from '@clavian/ui-web';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { useStatement, useWallets } from '../api/queries';
import { formatterFor } from '../lib/format';
import { LoadFailure, LoadingRegion, RowsSkeleton, SectionHead } from '../shell/feedback';
import { NavIcon } from '../shell/nav-icons';
import { Page } from '../shell/Page';

/** Wallet · Passes · Orders: the money group's own links, shown on a phone where the tab bar has one tab for them. */
export function MoneyTabs({ current }: { readonly current: 'wallet' | 'passes' | 'orders' }): ReactNode {
  const { t } = useTranslation('player');
  const items = [
    { key: 'wallet', to: '/wallet', label: t('nav.wallet') },
    { key: 'passes', to: '/passes', label: t('nav.passes') },
    { key: 'orders', to: '/orders', label: t('nav.orders') },
  ] as const;
  return (
    <nav className="pw-moneytabs" aria-label={t('nav.yourStuff')}>
      {items.map((item) => (
        <Link key={item.key} to={item.to} className="pw-moneytabs__item" aria-current={current === item.key ? 'page' : undefined}>
          {item.label}
        </Link>
      ))}
    </nav>
  );
}

export function WalletPage(): ReactNode {
  const { t } = useTranslation('player');
  const wallets = useWallets();
  const search = useSearch({ from: '/app/wallet' });
  const navigate = useNavigate();
  const list = wallets.data?.data ?? [];
  const selected = list.find((wallet) => wallet.organization.id === search.organization) ?? list[0];

  return (
    <Page title={t('wallet.title')} description={t('wallet.subtitle')}>
      <MoneyTabs current="wallet" />
      {wallets.isPending ? (
        <LoadingRegion className="pw-wallets">
          <Skeleton height={168} />
        </LoadingRegion>
      ) : wallets.isError ? (
        <LoadFailure error={wallets.error} onRetry={() => void wallets.refetch()} />
      ) : list.length === 0 ? (
        <div className="pw-panel">
          <EmptyState
            icon={<NavIcon name="wallet" size={32} />}
            title={t('wallet.none')}
            description={t('wallet.noneBody')}
            action={
              <Link to="/venues" className="pw-btn pw-btn--primary">
                {t('wallet.findVenue')}
              </Link>
            }
          />
        </div>
      ) : (
        <>
          {list.length > 1 ? (
            <SegmentedControl
              label={t('wallet.statement')}
              value={selected!.organization.id}
              onValueChange={(organization) => void navigate({ to: '/wallet', search: { organization }, replace: true })}
              options={list.map((wallet) => ({ value: wallet.organization.id, label: wallet.organization.name }))}
            />
          ) : null}
          {selected === undefined ? null : <WalletCard wallet={selected} />}
          {selected === undefined ? null : <Statement wallet={selected} />}
        </>
      )}
    </Page>
  );
}

function WalletCard({ wallet }: { readonly wallet: MyWallet }): ReactNode {
  const { t } = useTranslation('player');
  const fmt = formatterFor(wallet.total.currency);
  return (
    <section className="pw-walletcard" aria-labelledby="pw-wallet-org">
      <div className="pw-walletcard__top">
        <h2 className="pw-walletcard__org" id="pw-wallet-org">
          {wallet.organization.name}
        </h2>
        <Badge tone={wallet.useForPlay ? 'session' : 'neutral'}>{wallet.useForPlay ? t('wallet.useForPlay') : t('wallet.useForPlayOff')}</Badge>
      </div>
      <p className="pw-walletcard__label">{t('wallet.total')}</p>
      <p className="pw-walletcard__total clav-num">{fmt.money(wallet.total)}</p>
      <dl className="pw-walletcard__split">
        <div>
          <dt>{t('wallet.cash')}</dt>
          <dd className="clav-num">{fmt.money(wallet.cash)}</dd>
        </div>
        <div>
          <dt>{t('wallet.bonus')}</dt>
          <dd className="clav-num">{fmt.money(wallet.bonus)}</dd>
        </div>
      </dl>
      <p className="pw-walletcard__note">{t('wallet.usableAt', { organization: wallet.organization.name })}</p>
      {wallet.bonus.amount > 0 ? <p className="pw-walletcard__note">{t('wallet.bonusHint')}</p> : null}
      <div className="pw-walletcard__topup">
        <NavIcon name="plus" size={18} />
        <span>{t('wallet.topUp.body', { organization: wallet.organization.name })}</span>
      </div>
    </section>
  );
}

function Statement({ wallet }: { readonly wallet: MyWallet }): ReactNode {
  const { t } = useTranslation('player');
  const statement = useStatement(wallet.organization.id);
  const fmt = formatterFor(wallet.total.currency);
  const rows = statement.data?.pages.flatMap((page) => page.data) ?? [];

  return (
    <section className="pw-panel" aria-labelledby="pw-statement">
      <SectionHead id="pw-statement" title={t('wallet.statementFor', { organization: wallet.organization.name })} />
      {statement.isPending ? (
        <RowsSkeleton />
      ) : statement.isError ? (
        <LoadFailure error={statement.error} onRetry={() => void statement.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState size="compact" title={t('wallet.statementEmpty')} description={t('wallet.statementEmptyBody', { organization: wallet.organization.name })} />
      ) : (
        <>
          <ul className="pw-rows">
            {rows.map((row) => {
              const total = { amount: row.cashAmount.amount + row.bonusAmount.amount, currency: row.cashAmount.currency };
              const credit = total.amount >= 0;
              return (
                <li key={row.id} className="pw-row">
                  <span className="pw-row__icon" data-tone={credit ? 'credit' : 'debit'} aria-hidden="true">
                    <NavIcon name={credit ? 'plus' : 'wallet'} size={20} />
                  </span>
                  <span className="pw-row__main">
                    <span className="pw-row__title">{row.description}</span>
                    <span className="pw-row__meta">
                      {t(`wallet.kind.${row.kind}`)} · {fmt.dateTime(row.occurredAt, { date: 'medium', time: 'short' })}
                    </span>
                  </span>
                  <span className="pw-row__end clav-num" data-tone={credit ? 'credit' : 'debit'}>
                    {credit ? '+' : '−'}
                    {fmt.money({ amount: Math.abs(total.amount), currency: total.currency })}
                  </span>
                </li>
              );
            })}
          </ul>
          {statement.hasNextPage ? (
            <Button variant="secondary" block busy={statement.isFetchingNextPage} onClick={() => void statement.fetchNextPage()}>
              {t('wallet.more')}
            </Button>
          ) : null}
        </>
      )}
    </section>
  );
}
